import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import DashboardLayout from "../../../layouts/DashboardLayout";
import api from "../../../api";
import { Eye, EyeOff, UserPlus, Save, RefreshCw, Copy, Check, Upload, Image as ImageIcon, Sparkles } from "lucide-react";
import WeeklyScheduleEditor from "../../../components/WeeklyScheduleEditor";
import LanguageMultiSelect from "../../../components/LanguageMultiSelect";
import LineListInput from "../../../components/LineListInput";
import PublicationsEditor from "../../../components/PublicationsEditor";

const PASSWORD_CHARS = {
    upper: "ABCDEFGHIJKLMNOPQRSTUVWXYZ",
    lower: "abcdefghijklmnopqrstuvwxyz",
    digits: "0123456789",
    special: "!@#$%^&*()-_=+[]{}",
};

function generateStrongPassword() {
    const all = Object.values(PASSWORD_CHARS).join("");
    // Guarantee at least one of each category
    const guaranteed = Object.values(PASSWORD_CHARS).map(
        chars => chars[Math.floor(Math.random() * chars.length)]
    );
    const rest = Array.from({ length: 8 }, () => all[Math.floor(Math.random() * all.length)]);
    return [...guaranteed, ...rest].sort(() => Math.random() - 0.5).join("");
}

function getPasswordStrength(password) {
    if (!password) return null;
    const checks = [
        password.length >= 8,
        /[A-Z]/.test(password),
        /[a-z]/.test(password),
        /[0-9]/.test(password),
        /[^A-Za-z0-9]/.test(password),
        password.length >= 12,
    ];
    const score = checks.filter(Boolean).length;
    if (score <= 2) return { level: "Weak", color: "bg-red-400", textColor: "text-red-600", bars: 1 };
    if (score <= 3) return { level: "Fair", color: "bg-amber-400", textColor: "text-amber-600", bars: 2 };
    if (score <= 4) return { level: "Strong", color: "bg-blue-500", textColor: "text-blue-600", bars: 3 };
    return { level: "Very Strong", color: "bg-emerald-500", textColor: "text-emerald-600", bars: 4 };
}

function PasswordStrengthBar({ password }) {
    const strength = getPasswordStrength(password);
    if (!strength) return null;

    const hints = [
        { ok: password.length >= 8, label: "8+ chars" },
        { ok: /[A-Z]/.test(password), label: "Uppercase" },
        { ok: /[a-z]/.test(password), label: "Lowercase" },
        { ok: /[0-9]/.test(password), label: "Number" },
        { ok: /[^A-Za-z0-9]/.test(password), label: "Symbol" },
    ];

    return (
        <div className="mt-2 space-y-2">
            <div className="flex gap-1">
                {[1, 2, 3, 4].map(i => (
                    <div
                        key={i}
                        className={`h-1.5 flex-1 rounded-full transition-all duration-300 ${i <= strength.bars ? strength.color : "bg-secondary-100"}`}
                    />
                ))}
            </div>
            <div className="flex items-center justify-between">
                <span className={`text-xs font-semibold ${strength.textColor}`}>{strength.level}</span>
                <div className="flex gap-2">
                    {hints.map(h => (
                        <span key={h.label} className={`text-xs ${h.ok ? "text-emerald-600" : "text-secondary-300"}`}>
                            {h.ok ? "✓" : "·"} {h.label}
                        </span>
                    ))}
                </div>
            </div>
        </div>
    );
}

const REQUIRED_FIELDS = ["name", "contact", "email", "specialized", "experience", "degree"];

const DEFAULT_SPECIALIZATIONS = [
    "Psychologists",
    "Clinical Psychologist",
    "Psychotherapy",
    "Psychiatry",
    "Plastic And Cosmetic Surgery",
    "Urology",
    "Ophthalmology",
    "Obstetrics and Gynaecology",
    "Diabetes and Endocrinology",
    "Internal Medicine",
    "Neonatology & NICU",
    "Rheumatology",
    "Paediatric And Child Care",
    "Dermatology",
    "Orthopaedics",
    "Nephrology",
    "Ear Nose Throat",
    "Lactation",
    "Cardiology",
    "Dental Medicine",
    "Pulmonology (Respiratory and Sleep Medicine)",
    "Gastrointestinal Science",
    "Cardiothoracic Vascular Surgery",
    "Cancer Care/Oncology",
    "Hemato Oncology",
    "Bariatric Surgery",
    "Renal Sciences",
    "Pathology",
    "Spine Care",
    "Vascular and Endovascular Surgery",
    "Anesthesiology",
    "General Surgery",
    "Neurology",
    "Neurosurgery",
    "Liver Transplantation Surgery",
    "Interventional Radiology",
    "Nutrition And Dietetics",
];

function FieldError({ msg }) {
    if (!msg) return null;
    return <p className="text-xs text-red-500 mt-1">{msg}</p>;
}

function Label({ children, required }) {
    return (
        <label className="block text-xs font-semibold text-secondary-600 mb-1.5">
            {children}
            {required && <span className="text-red-500 ml-0.5">*</span>}
        </label>
    );
}

export default function DoctorForm() {
    const { id } = useParams();
    const navigate = useNavigate();

    const [loading, setLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [copied, setCopied] = useState(false);
    const [errors, setErrors] = useState({});
    const [serverError, setServerError] = useState(null);
    const [imageFile, setImageFile] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);
    const [uploadingImage, setUploadingImage] = useState(false);
    const [specializationList, setSpecializationList] = useState([]);
    const [generatingBio, setGeneratingBio] = useState(false);

    useEffect(() => {
        api.fetchSpecializations()
            .then(res => {
                const list = res.data?.data;
                if (Array.isArray(list) && list.length > 0) {
                    setSpecializationList(list);
                }
            })
            .catch(err => {
                console.error("Failed to fetch specializations:", err);
            });
    }, []);

    const [formData, setFormData] = useState({
        name: "",
        contact: "",
        email: "",
        password: "",
        specialized: "",
        experience: "",
        degree: "",
        registration_number: "",
        bio: "",
        gender: "Miss.", // Default gender
        languages: [],
        expertise: [],
        memberships: [],
        publications: [],
        weekly_schedule: { slot_duration: 30 },
        profile_image_key: null,
    });

    useEffect(() => {
        if (id) fetchDoctor(id);
    }, [id]);

    const fetchDoctor = async (doctorId) => {
        try {
            const res = await api.getDoctor(doctorId);
            const data = res.data;
            setFormData({
                name: data.name || "",
                contact: data.phone_number || data.contact || "",
                email: data.email || "",
                password: "",
                specialized: data.specialized || "",
                experience: data.experience || "",
                degree: data.degree || "",
                registration_number: data.registration_number || "",
                bio: data.bio || "",
                gender: data.gender || "Miss.",
                languages: data.languages || [],
                expertise: data.expertise || [],
                memberships: data.memberships || [],
                publications: data.publications || [],
                weekly_schedule: data.weekly_schedule || { slot_duration: 30 },
                profile_image_key: data.profile_image_key || null,
            });
            if (data.profile_image_url) {
                setImagePreview(data.profile_image_url);
            }
        } catch (err) {
            console.error("Error fetching doctor:", err);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        if (errors[name]) setErrors(prev => ({ ...prev, [name]: null }));
    };

    const handleGenerateBio = async () => {
        if (!formData.specialized && !formData.degree) {
            setErrors(prev => ({ ...prev, bio: "Add a specialization or degree first so AI has something to write about." }));
            return;
        }
        if (formData.bio && formData.bio.trim() && !window.confirm("This will replace the current bio text. Continue?")) {
            return;
        }

        setGeneratingBio(true);
        setErrors(prev => ({ ...prev, bio: null }));
        try {
            const res = await api.generateDoctorBio({
                name: formData.name,
                specialization: formData.specialized,
                degree: formData.degree,
                experience_years: formData.experience,
                languages: formData.languages,
                gender: formData.gender,
                expertise: formData.expertise,
                memberships: formData.memberships,
                publications: formData.publications,
            });
            setFormData(prev => ({ ...prev, bio: (res.data.bio || "").slice(0, 600) }));
        } catch (err) {
            console.error("Failed to generate bio:", err);
            setErrors(prev => ({ ...prev, bio: err.response?.data?.error || "Failed to generate bio. Try again." }));
        } finally {
            setGeneratingBio(false);
        }
    };

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            if (file.size > 5 * 1024 * 1024) {
                setErrors(prev => ({ ...prev, image: "Image size must be less than 5MB" }));
                return;
            }
            setImageFile(file);
            setImagePreview(URL.createObjectURL(file));
            setErrors(prev => ({ ...prev, image: null }));
        }
    };

    const validate = () => {
        const newErrors = {};
        const required = [...REQUIRED_FIELDS];
        if (!id) required.push("password");

        required.forEach(field => {
            if (!formData[field]?.toString().trim()) {
                newErrors[field] = "This field is required.";
            }
        });

        if (formData.contact && formData.contact.replace(/\D/g, "").length !== 10) {
            newErrors.contact = "Enter a valid 10-digit mobile number.";
        }
        if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
            newErrors.email = "Enter a valid email address.";
        }
        if (formData.experience && (isNaN(formData.experience) || Number(formData.experience) < 0)) {
            newErrors.experience = "Enter a valid number of years.";
        }

        return newErrors;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setServerError(null);
        const validationErrors = validate();
        if (Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors);
            return;
        }

        setLoading(true);
        setUploadingImage(true);
        try {
            let finalData = { ...formData };
            if (imageFile) {
                const imgData = new FormData();
                imgData.append("image", imageFile);
                const uploadRes = await api.uploadDoctorImage(imgData);
                if (uploadRes.data && uploadRes.data.profile_image_key) {
                    finalData.profile_image_key = uploadRes.data.profile_image_key;
                }
            }
            setUploadingImage(false);

            const res = id ? await api.updateDoctor(id, finalData) : await api.createDoctor(finalData);
            if (res.status === 400) {
                setServerError(res.data?.error || "Email or phone number is already registered in this organization.");
            } else if (res.status === 201 || res.status === 200) {
                navigate("/doctor");
            } else {
                setServerError("Something went wrong. Please try again.");
            }
        } catch (err) {
            if (err.response?.status === 400) {
                setServerError(err.response?.data?.error || "Email or phone number is already registered in this organization.");
            } else {
                setServerError(err.response?.data?.error || "Something went wrong. Please try again.");
            }
        } finally {
            setLoading(false);
            setUploadingImage(false);
        }
    };

    const inputClass = (field) =>
        `w-full border rounded-lg px-3 py-2.5 text-sm text-secondary-800 bg-white focus:outline-none focus:ring-2 transition-colors ${
            errors[field]
                ? "border-red-300 focus:ring-red-200"
                : "border-secondary-200 focus:ring-primary-300"
        }`;

    return (
        <DashboardLayout>
            <div className="max-w-5xl mx-auto px-4 py-8">
                {/* Back Button */}
                <div className="mb-4">
                    <button
                        type="button"
                        onClick={() => navigate("/doctor")}
                        className="inline-flex items-center gap-2 text-gray-500 hover:text-gray-800 transition-colors"
                    >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                        </svg>
                        <span className="font-medium">Back to Clinicians</span>
                    </button>
                </div>
                <div className="bg-white rounded-2xl border border-secondary-100 shadow-sm overflow-hidden">
                    {/* Header */}
                    <div className="px-6 py-5 border-b border-secondary-100 flex items-center gap-3">
                        <div className="w-9 h-9 bg-primary-100 rounded-xl flex items-center justify-center">
                            {id ? <Save className="w-5 h-5 text-primary-600" /> : <UserPlus className="w-5 h-5 text-primary-600" />}
                        </div>
                        <div>
                            <h2 className="text-lg font-bold text-secondary-900">{id ? "Edit Clinician" : "Add New Clinician"}</h2>
                            <p className="text-xs text-secondary-400">Fields marked <span className="text-red-500">*</span> are required</p>
                        </div>
                    </div>

                    <form onSubmit={handleSubmit} noValidate className="p-6 space-y-8">
                        {serverError && (
                            <div className="bg-red-50 border border-red-100 text-red-700 rounded-lg px-4 py-3 text-sm col-span-1 md:col-span-2">
                                {serverError}
                            </div>
                        )}

                        <div className="grid grid-cols-1 md:grid-cols-12 gap-8">
                            <div className="md:col-span-8 grid grid-cols-1 sm:grid-cols-2 gap-5 content-start">
                                                        {/* Prefix / Gender */}
                        <div>
                            <Label>Prefix</Label>
                            <select
                                name="gender"
                                value={formData.gender}
                                onChange={handleChange}
                                className={inputClass("gender")}
                            >
                                <option value="Mr.">Mr.</option>
                                <option value="Miss.">Miss.</option>
                                <option value="Mrs.">Mrs.</option>
                                <option value="Other">Other</option>
                            </select>
                        </div>
                        
                        {/* Name */}
                        <div>
                            <Label required>Full Name</Label>
                            <input
                                type="text"
                                name="name"
                                placeholder="e.g. Dr. Jane Smith"
                                value={formData.name}
                                onChange={handleChange}
                                className={inputClass("name")}
                            />
                            <FieldError msg={errors.name} />
                        </div>

                        {/* Contact */}
                        <div>
                            <Label required>Contact Number</Label>
                            <input
                                type="text"
                                name="contact"
                                placeholder="e.g. +91 98765 43210"
                                value={formData.contact}
                                onChange={handleChange}
                                className={inputClass("contact")}
                            />
                            <FieldError msg={errors.contact} />
                        </div>

                        {/* Email */}
                        <div>
                            <Label required>Email Address</Label>
                            <input
                                type="email"
                                name="email"
                                placeholder="e.g. jane@clinic.com"
                                value={formData.email}
                                onChange={handleChange}
                                className={inputClass("email")}
                            />
                            <FieldError msg={errors.email} />
                        </div>

                        {/* Password — new only */}
                        {!id && (
                            <div>
                                <div className="flex items-center justify-between mb-1.5">
                                    <span className="text-xs font-semibold text-secondary-600">Password <span className="text-red-500">*</span></span>
                                    <div className="flex gap-2">
                                        <button
                                            type="button"
                                            title="Generate strong password"
                                            onClick={() => {
                                                const pwd = generateStrongPassword();
                                                setFormData(prev => ({ ...prev, password: pwd }));
                                                setShowPassword(true);
                                                if (errors.password) setErrors(prev => ({ ...prev, password: null }));
                                            }}
                                            className="flex items-center gap-1 text-xs text-primary-600 hover:text-primary-700 font-semibold"
                                        >
                                            <RefreshCw size={12} /> Generate
                                        </button>
                                        {formData.password && (
                                            <button
                                                type="button"
                                                title="Copy password"
                                                onClick={() => {
                                                    navigator.clipboard.writeText(formData.password);
                                                    setCopied(true);
                                                    setTimeout(() => setCopied(false), 2000);
                                                }}
                                                className="flex items-center gap-1 text-xs text-secondary-500 hover:text-secondary-700 font-semibold"
                                            >
                                                {copied ? <><Check size={12} className="text-emerald-500" /> Copied</> : <><Copy size={12} /> Copy</>}
                                            </button>
                                        )}
                                    </div>
                                </div>
                                <div className="relative">
                                    <input
                                        type={showPassword ? "text" : "password"}
                                        name="password"
                                        placeholder="Set a login password"
                                        value={formData.password}
                                        onChange={handleChange}
                                        className={`${inputClass("password")} pr-10`}
                                    />
                                    <button
                                        type="button"
                                        className="absolute right-3 top-1/2 -translate-y-1/2 text-secondary-400 hover:text-secondary-600"
                                        onClick={() => setShowPassword(p => !p)}
                                    >
                                        {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                                    </button>
                                </div>
                                <PasswordStrengthBar password={formData.password} />
                                <FieldError msg={errors.password} />
                            </div>
                        )}

                        {/* Specialization */}
                        <div>
                            <Label required>Specialization</Label>
                            <select
                                name="specialized"
                                value={formData.specialized}
                                onChange={handleChange}
                                className={inputClass("specialized")}
                            >
                                <option value="">Select specialization...</option>
                                {specializationList.length > 0
                                    ? specializationList.map(s => (
                                        <option key={s.id || s.name} value={s.name}>
                                            {s.name} {!s.canPrescribeMedicine ? " (Non-Prescribing)" : ""}
                                        </option>
                                    ))
                                    : DEFAULT_SPECIALIZATIONS.map(s => (
                                        <option key={s} value={s}>{s}</option>
                                    ))
                                }
                            </select>
                            <FieldError msg={errors.specialized} />
                        </div>

                        {/* Medical Registration ID / License No */}
                        <div>
                            <Label>Medical Registration ID / License No.</Label>
                            <input
                                type="text"
                                name="registration_number"
                                value={formData.registration_number}
                                onChange={handleChange}
                                placeholder="e.g. MCI-78452 or State Medical Council Reg No."
                                className={inputClass("registration_number")}
                            />
                            <FieldError msg={errors.registration_number} />
                        </div>

                        {/* Experience */}
                        <div>
                            <Label required>Experience (years)</Label>
                            <input
                                type="number"
                                name="experience"
                                placeholder="e.g. 5"
                                min="0"
                                max="99"
                                value={formData.experience}
                                onChange={(e) => {
                                    if (e.target.value.length <= 2) {
                                        handleChange(e);
                                    }
                                }}
                                onWheel={e => e.target.blur()}
                                className={inputClass("experience")}
                            />
                            <FieldError msg={errors.experience} />
                        </div>

                        {/* Degree / Certification */}
                        <div className="sm:col-span-2">
                            <Label required>Certification / Degree</Label>
                            <textarea
                                name="degree"
                                value={formData.degree}
                                onChange={handleChange}
                                maxLength={480}
                                rows={3}
                                placeholder="e.g. MD, MBBS&#10;B.A in psychology&#10;MA in psychology"
                                className={`${inputClass("degree")} resize-y`}
                            />
                            <p className="text-[11px] text-secondary-500 mt-1">
                                Multiple degrees or qualifications can be entered on separate lines (press Enter).
                            </p>
                            <FieldError msg={errors.degree} />
                        </div>

                        {/* Languages Spoken */}
                        <div className="sm:col-span-2">
                            <Label>Languages Spoken</Label>
                            <LanguageMultiSelect
                                value={formData.languages}
                                onChange={(newLanguages) => setFormData(prev => ({ ...prev, languages: newLanguages }))}
                                placeholder="Select or type languages spoken (e.g. English, Hindi, Tamil)..."
                            />
                            <p className="text-[11px] text-secondary-500 mt-1">
                                Select all languages this clinician can communicate in during consultations.
                            </p>
                        </div>

                        {/* Field of Expertise */}
                        <div className="sm:col-span-2">
                            <Label>Field of Expertise</Label>
                            <LineListInput
                                value={formData.expertise}
                                onChange={(newExpertise) => setFormData(prev => ({ ...prev, expertise: newExpertise }))}
                                placeholder={"e.g. General Surgery\nBreast Conservative Surgery\nSentinel Lymph Node Biopsy"}
                            />
                            <p className="text-[11px] text-secondary-500 mt-1">
                                One area per line (press Enter). Specific areas this clinician specializes in, beyond their primary specialization.
                            </p>
                        </div>

                        {/* Fellowships & Memberships */}
                        <div className="sm:col-span-2">
                            <Label>Fellowships & Memberships</Label>
                            <LineListInput
                                value={formData.memberships}
                                onChange={(newMemberships) => setFormData(prev => ({ ...prev, memberships: newMemberships }))}
                                placeholder={"e.g. Member of Association of Surgeons of India\nMember of the Surgical Society of Bangalore"}
                            />
                            <p className="text-[11px] text-secondary-500 mt-1">
                                One membership per line (press Enter). Professional bodies, fellowships or societies this clinician belongs to.
                            </p>
                        </div>

                        {/* Talks & Publications */}
                        <div className="sm:col-span-2">
                            <Label>Talks & Publications</Label>
                            <PublicationsEditor
                                value={formData.publications}
                                onChange={(newPublications) => setFormData(prev => ({ ...prev, publications: newPublications }))}
                            />
                            <p className="text-[11px] text-secondary-500 mt-1">
                                Any talks given or papers published, with an optional link to each.
                            </p>
                        </div>

                        {/* Short Bio */}
                        <div className="sm:col-span-2">
                            <Label>Short Bio</Label>
                            <div className="relative">
                                <textarea
                                    name="bio"
                                    value={formData.bio}
                                    onChange={handleChange}
                                    maxLength={600}
                                    rows={5}
                                    placeholder="A brief introduction patients will see on the doctor's profile (approach, focus areas, experience highlights). Or click Generate with AI below to draft one automatically."
                                    className={`${inputClass("bio")} resize-y pb-10`}
                                />
                                <button
                                    type="button"
                                    onClick={handleGenerateBio}
                                    disabled={generatingBio}
                                    title="Draft a bio automatically from this doctor's specialization, degree, experience, languages, expertise, memberships and publications"
                                    className="absolute right-2.5 bottom-2.5 inline-flex items-center gap-1.5 text-xs font-semibold text-primary-700 bg-primary-50 hover:bg-primary-100 border border-primary-200 disabled:opacity-50 disabled:cursor-not-allowed rounded-full px-3 py-1.5 shadow-sm transition-colors"
                                >
                                    <Sparkles className={`w-3.5 h-3.5 ${generatingBio ? "animate-pulse" : ""}`} />
                                    {generatingBio ? "Generating..." : "Generate with AI"}
                                </button>
                            </div>
                            <p className="text-[11px] text-secondary-500 mt-1">
                                {formData.bio?.length || 0}/600 characters
                            </p>
                            <FieldError msg={errors.bio} />
                        </div>
                            </div>

                            {/* Profile Image */}
        <div className="md:col-span-4 flex flex-col">
            <Label>Profile Image</Label>
            <div className="mt-1 flex-1 min-h-[250px] border-2 border-dashed border-secondary-200 rounded-xl bg-secondary-50 hover:bg-secondary-100 transition-colors relative overflow-hidden flex items-center justify-center group">
                {imagePreview ? (
                    <img src={imagePreview} alt="Preview" className="absolute inset-0 w-full h-full object-cover" />
                ) : (
                    <div className="flex flex-col items-center p-6 text-center">
                        <ImageIcon className="w-10 h-10 text-secondary-300 mb-3 group-hover:scale-110 transition-transform" />
                        <span className="text-sm font-semibold text-secondary-700">Click to upload photo</span>
                        <span className="text-xs text-secondary-400 mt-1">PNG, JPG up to 5MB</span>
                    </div>
                )}
                <input
                    type="file"
                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                    accept="image/png, image/jpeg, image/jpg"
                    onChange={handleImageChange}
                />
            </div>
            <FieldError msg={errors.image} />
        </div>
                        </div>





                        {/* Weekly Schedule & Rate Chart */}
                        <div className="">
                            <WeeklyScheduleEditor 
                                schedule={formData.weekly_schedule} 
                                onChange={(newSchedule) => setFormData(prev => ({ ...prev, weekly_schedule: newSchedule }))} 
                            />
                        </div>

                        {/* Actions */}
                        <div className="flex justify-end gap-3 pt-6 border-t border-secondary-100">
                            <button
                                type="button"
                                onClick={() => navigate("/doctor")}
                                className="px-5 py-2.5 border border-secondary-200 text-secondary-600 rounded-lg text-sm font-semibold hover:bg-secondary-50 transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                disabled={loading}
                                className="flex items-center gap-2 px-5 py-2.5 bg-primary-600 hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg text-sm font-semibold transition-colors"
                            >
                                {id ? <Save className="w-4 h-4" /> : <UserPlus className="w-4 h-4" />}
                                {uploadingImage ? "Uploading Image..." : loading ? "Saving..." : id ? "Update Clinician" : "Create Clinician"}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </DashboardLayout>
    );
}
